import React from "react";
import PreviewTable from "./PreviewTable";
import { columnLabel } from "./helpers";

/**
 * Runs the current query and shows a paginated table of results.
 * @param {object} props
 * @param {object} props.preview the preview state ({ rows, pagination, loading, error }).
 * @param {(page: number) => void} props.onRun runs the preview at a given page.
 * @param {boolean} props.disabled disables running (e.g. no fields chosen).
 * @param {Array<{key: string, label: string, type?: string}>} [props.fields] the
 *   dataset's field metadata, used to title and format the columns.
 * @returns {JSX.Element}
 */
export default function PreviewPanel({ preview, onRun, disabled, fields = [] }) {
  const { rows, pagination, loading, error } = preview;
  const fieldByKey = new Map(fields.map((f) => [f.key, f]));
  const columns = rows.length
    ? Object.keys(rows[0]).map((key) => ({
        key,
        label: columnLabel(key, fieldByKey),
        type: fieldByKey.get(key)?.type,
      }))
    : [];

  return (
    <div className="qb-preview">
      <div className="qb-preview-head">
        <h3 className="qb-subtitle">Preview</h3>
        <button
          type="button"
          className="qb-btn qb-btn-primary"
          onClick={() => onRun(1)}
          disabled={disabled || loading}
        >
          {loading ? "Running…" : "Run preview"}
        </button>
      </div>

      {error && (
        <div className="qb-error" role="alert">
          {error}
        </div>
      )}

      {!error && pagination && <PreviewTable rows={rows} columns={columns} />}

      {pagination && pagination.total > 0 && (
        <div className="qb-pagination">
          <button
            type="button"
            className="qb-btn qb-btn-ghost"
            disabled={pagination.page <= 1 || loading}
            onClick={() => onRun(pagination.page - 1)}
          >
            Prev
          </button>
          <span className="qb-muted">
            Page {pagination.page} of {pagination.totalPages} · {pagination.total}{" "}
            rows
          </span>
          <button
            type="button"
            className="qb-btn qb-btn-ghost"
            disabled={pagination.page >= pagination.totalPages || loading}
            onClick={() => onRun(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
